import $ from 'jquery'

import { modifyAndMaintainFocus } from '@app/utils/domHelpers'
import type Tooltip from '@app/setup/tooltip/uikit-tooltip'
import { SfLog } from '@app/adapters/logger'

type IconType = 'validation' | 'companyDefault'

export const LIVE_INPUT_WRAPPER_CLASS = 'live-input-wrapper'
export const LIVE_INPUT_ICON_WRAPPER_CLASS = `${LIVE_INPUT_WRAPPER_CLASS}__icon-wrapper`
export const LIVE_INPUT_WRAPPER_ICON_COUNT_CLASS_PREFIX = 'icons-count'

function addTooltip(icon: HTMLElement, error: string): void {
  icon.setAttribute('title', error)
  icon.setAttribute('rel', 'tooltip')
  icon.setAttribute('data-toggle', 'tooltip')
  icon.setAttribute('data-tooltip-size', 'sm')
}

function removeTooltip(icon: Element): void {
  icon.removeAttribute('data-original-title')
  icon.removeAttribute('rel')
  icon.removeAttribute('data-toggle')
  icon.removeAttribute('data-tooltip-size')

  // that's the only way to get access to the tooltip instance - using JQuery
  const tooltip = $(icon).data('tooltip-instance') as Tooltip

  if (!tooltip) {
    return
  }

  try {
    tooltip.dispose()
  } catch (error) {
    SfLog.error('Error disposing tooltip', { error })
  }

  $(icon).removeData('tooltip-instance')
}

function createIcon(type: IconType, tooltip: string): HTMLElement {
  const icon = document.createElement('i')

  icon.classList.add(`live-input-icon--${type}`)

  if (type === 'validation') {
    icon.classList.add(
      'fa',
      'fa-solid',
      'sf-c-danger-light-1',
      'i-unreconciled'
    )
  } else if (type === 'companyDefault') {
    icon.classList.add(
      'sf-icon-liquid__company-link',
      'sf-icon-liquid__company-link--inline'
    )
  }

  icon.setAttribute('data-type', type)
  addTooltip(icon, tooltip)

  return icon
}

/**
 * Checks if the element is the only one rendering inside a wrapper element.
 * This is used to determine if the wrapper should be styled as full-width.
 */
function isWrapperOnlyRenderingElement(
  el: HTMLElement,
  wrapperElement: HTMLElement
): boolean {
  if (!wrapperElement) {
    return false
  }

  // Clone the wrapperElement to avoid mutating the original
  const clonedWrapperElement = wrapperElement.cloneNode(true) as HTMLElement
  // Remove the target element from the clone
  const elIndex = Array.from(wrapperElement.children).indexOf(el)
  if (elIndex >= 0) {
    clonedWrapperElement.removeChild(clonedWrapperElement.children[elIndex])
  }

  // Check if there's any non-empty text left in the wrapperElement (excluding whitespace)
  const text = clonedWrapperElement.textContent?.trim()

  return text?.length === 0
}

export function createInputWrappers(input: HTMLElement): {
  wrapper: HTMLElement
  iconWrapper: HTMLElement
} {
  const wrapper = document.createElement('div')

  const { textAlign, position } = window.getComputedStyle(input)

  wrapper.classList.add(LIVE_INPUT_WRAPPER_CLASS)

  const parentTdElement = input.closest('td')

  if (textAlign === 'right') {
    wrapper.classList.add(`${LIVE_INPUT_WRAPPER_CLASS}--right`)
  }

  if (position === 'static') {
    wrapper.classList.add(`${LIVE_INPUT_WRAPPER_CLASS}--relative`)
  }

  // If the input IS inside a <td> element and it IS the only element rendering inside it,
  // OR if the element IS a textarea,
  // we want to apply full-width styling to the input-wrapper
  const hasParentTdRenderingInputOnly =
    parentTdElement && isWrapperOnlyRenderingElement(input, parentTdElement)
  if (hasParentTdRenderingInputOnly || input instanceof HTMLTextAreaElement) {
    wrapper.classList.add(`${LIVE_INPUT_WRAPPER_CLASS}--full-width`)
  }

  // If the input IS inside a <td> element and it IS NOT the only element rendering inside it,
  // OR if the element IS a select,
  // we want to apply inline styling to the input-wrapper
  const hasParentTdRenderingMixedInputText =
    parentTdElement && !isWrapperOnlyRenderingElement(input, parentTdElement)
  if (
    !hasParentTdRenderingInputOnly &&
    (hasParentTdRenderingMixedInputText || input instanceof HTMLSelectElement)
  ) {
    wrapper.classList.add(`${LIVE_INPUT_WRAPPER_CLASS}--inline`)
  }

  const iconWrapper = document.createElement('div')
  iconWrapper.classList.add(LIVE_INPUT_ICON_WRAPPER_CLASS)

  if (textAlign === 'right') {
    iconWrapper.classList.add(`${LIVE_INPUT_ICON_WRAPPER_CLASS}--right`)
  }

  return { wrapper, iconWrapper }
}

function setIconsCountClass(wrapper: HTMLElement, iconCount: number): void {
  Array.from(wrapper.classList)
    .filter((className) =>
      className.startsWith(`${LIVE_INPUT_WRAPPER_ICON_COUNT_CLASS_PREFIX}-`)
    )
    .forEach((className) => wrapper.classList.remove(className))
  wrapper.classList.add(
    `${LIVE_INPUT_WRAPPER_ICON_COUNT_CLASS_PREFIX}-${iconCount}`
  )
}

export function addIcon(
  el: HTMLElement,
  type: IconType,
  tooltip: string
): void {
  let wrapper = null
  let iconWrapper

  if (
    el.parentNode instanceof HTMLElement &&
    el.parentNode.classList.contains(LIVE_INPUT_WRAPPER_CLASS)
  ) {
    wrapper = el.parentNode
  }

  if (!wrapper) {
    const { wrapper: newWrapper, iconWrapper: newIconWrapper } =
      createInputWrappers(el)

    wrapper = newWrapper
    iconWrapper = newIconWrapper

    el.parentNode?.insertBefore(wrapper, el)

    wrapper.appendChild(el)
    wrapper.appendChild(iconWrapper)
  } else {
    iconWrapper = wrapper.querySelector(`.${LIVE_INPUT_ICON_WRAPPER_CLASS}`)
  }

  if (!iconWrapper) {
    return
  }

  const icon = createIcon(type, tooltip)
  iconWrapper.appendChild(icon)

  setIconsCountClass(wrapper, iconWrapper.children.length)

  $(document).trigger('tooltipsRendered', [icon.parentElement, icon])
}

export function removeIcon(el: HTMLElement, type: IconType): void {
  const wrapper = el.parentNode

  if (
    !(
      wrapper instanceof HTMLElement &&
      wrapper.classList.contains(LIVE_INPUT_WRAPPER_CLASS)
    )
  ) {
    return
  }

  const iconWrapper = wrapper.querySelector(`.${LIVE_INPUT_ICON_WRAPPER_CLASS}`)

  if (!iconWrapper) {
    return
  }

  const icon = iconWrapper.querySelector(`*[data-type="${type}"]`)

  if (!icon) {
    return
  }

  removeTooltip(icon)
  iconWrapper.removeChild(icon)

  setIconsCountClass(wrapper, iconWrapper.children.length)
}

export const showExternalCompanyDefault = (
  input: HTMLInputElement,
  companyName: string
): void => {
  if (input.getAttribute('data-company-default') === 'true') {
    return
  }

  modifyAndMaintainFocus(() => {
    window.requestAnimationFrame(() => {
      addIcon(
        input,
        'companyDefault',
        window.I18n.t('js.live_inputs.tooltips.external_company_default', {
          company_name: companyName,
        })
      )
    })
  }, input)

  input.setAttribute('data-company-default', 'true')
}
